// Autoplays a muted looping video for everyone and wires a [data-toggle] button so shoppers can pause or resume it.
// Retries play once the video has data and whenever it scrolls into view, in case the browser held autoplay back.
class SVideoToggle extends HTMLElement {
  #video;
  #button;
  #ac;
  #observer;
  #userPaused = false;

  connectedCallback() {
    this.#video = this.querySelector('video');
    this.#button = this.querySelector('[data-toggle]');
    if (!this.#video) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#video.muted = true;
    this.#button?.addEventListener('click', () => {
      if (this.#video.paused) {
        this.#userPaused = false;
        this.#play();
      } else {
        this.#userPaused = true;
        this.#video.pause();
      }
    }, { signal });
    this.#video.addEventListener('play', () => this.#sync(), { signal });
    this.#video.addEventListener('pause', () => this.#sync(), { signal });
    this.#video.addEventListener('loadeddata', () => this.#play(), { signal });
    this.#video.addEventListener('ended', () => this.#sync(), { signal });

    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.#play();
    });
    this.#observer.observe(this.#video);

    // data-lazy: wait until the video scrolls into view before loading and playing it.
    if (!this.hasAttribute('data-lazy')) this.#play();
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #play() {
    if (this.#userPaused || !this.#video.paused) return;
    this.#video.play().catch(() => {});
  }

  #sync() {
    if (!this.#button) return;
    const playing = !this.#video.paused;
    this.toggleAttribute('data-playing', playing);
    this.#button.setAttribute('aria-pressed', String(playing));
    this.#button.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
    this.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
    this.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
  }
}

if (!customElements.get('s-video-toggle')) {
  customElements.define('s-video-toggle', SVideoToggle);
}
